import { unwrap } from "@sagot/api-client";
import { BACKUP_CODE_PATTERN, CONSOLE_PATHS, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Avatar, Button, Input, toast } from "@sagot/ui";
import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import { Navigate } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { clearPendingAuth, readPendingAuth } from "../session/pending-auth";
import { useApi } from "../session/session-provider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { IconBadge } from "./components/icon-badge";
import { OtpInput, isCompleteOtp } from "./components/otp-input";
import { useCompleteSignIn } from "./use-sign-in";

type Mode = "app" | "backup";

/** Who is signing in, with the way out when it is the wrong account (a pill like the shell's inputs). */
function Identity({ email }: { email: string }) {
  const { t } = useTranslation();
  return (
    <div className="a-id">
      <Avatar name={email} />
      <span className="a-id-mail">{email}</span>
      <AppLink href={CONSOLE_PATHS.login} onClick={clearPendingAuth}>
        {t("auth.twoFactor.notYou")}
      </AppLink>
    </div>
  );
}

/** Lost phone and backup codes: asks CRUD.IT Support for a recovery (the mockup's `.a-staff` footer row). */
function RecoveryHelp({ challengeId }: { challengeId: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const request = async () => {
    try {
      await unwrap(api.POST("/v1/auth/2fa/recovery-request", { body: { challenge_id: challengeId } }));
      toast.info(t("auth.twoFactor.recoverySent"));
    } catch (caught) {
      toast.error(message(caught));
    }
  };
  return (
    <div className="a-staff a-staff-help">
      <span>
        {t("auth.twoFactor.helpLead")}{" "}
        <button type="button" onClick={() => void request()}>
          {t("auth.twoFactor.helpLink")}
        </button>
        .
      </span>
    </div>
  );
}

export function TwoFactorPage() {
  const { t } = useTranslation();
  const api = useApi();
  const pending = readPendingAuth();
  const completeSignIn = useCompleteSignIn();
  const message = useErrorMessage();
  const [mode, setMode] = useState<Mode>("app");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [trust, setTrust] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const backupRef = useRef<HTMLInputElement>(null);
  const switched = useRef(false);

  useEffect(() => {
    if (!switched.current) return;
    if (mode === "backup") backupRef.current?.focus();
    else document.getElementById("tf-1")?.focus();
  }, [mode]);

  if (pending?.kind !== "two-factor") return <Navigate to={CONSOLE_PATHS.login} replace />;
  const ready = mode === "app" ? isCompleteOtp(code) : BACKUP_CODE_PATTERN.test(backup.trim().toUpperCase());

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setError(null);
    try {
      const credential = mode === "app" ? { code } : { backup_code: backup.trim().toUpperCase() };
      const body = await unwrap(api.POST("/v1/auth/2fa/verify", { body: { challenge_id: pending.challengeId, trust_device: trust, ...credential } }));
      await completeSignIn(body, { email: pending.email, next: pending.next });
    } catch (caught) {
      setError(message(caught));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMode = () => {
    switched.current = true;
    setError(null);
    setMode((m) => (m === "app" ? "backup" : "app"));
  };

  return (
    <AuthLayout title={t("auth.twoFactor.title", { product: PRODUCT_NAME })} back={{ label: t("auth.twoFactor.differentAccount"), href: CONSOLE_PATHS.login }}>
      <IconBadge icon="shield-check" />
      <h1 className="a-title">{t("auth.twoFactor.heading")}</h1>
      <p className="a-sub a-sub-tight" aria-live="polite">
        {mode === "app" ? (
          <>
            {t("auth.twoFactor.appSubLead")} <strong>{`${PRODUCT_NAME} · ${pending.email.split("@")[0] ?? pending.email}`}</strong>.
          </>
        ) : (
          t("auth.twoFactor.backupSub")
        )}
      </p>
      <Identity email={pending.email} />
      {error ? (
        <FormAlert
          title={error}
          onDismiss={() => {
            setError(null);
          }}
        />
      ) : null}
      <form noValidate onSubmit={(event) => void onSubmit(event)}>
        {mode === "app" ? (
          <div className="a-fld a-fld-code">
            <label className="label" htmlFor="tf-1">
              {t("auth.twoFactor.codeLabel")}
            </label>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the code is the page's only task (mockup autofocus) */}
            <OtpInput id="tf-1" value={code} onChange={setCode} autoFocus invalid={Boolean(error)} describedBy="tf-hint" />
            <p className="hint" id="tf-hint">
              {t("auth.twoFactor.codeHint")}
            </p>
          </div>
        ) : (
          <div className="a-fld a-fld-code">
            <label className="label" htmlFor="tf-backup-input">
              {t("auth.twoFactor.backupLabel")}
            </label>
            <Input
              ref={backupRef}
              id="tf-backup-input"
              className="a-code"
              placeholder="XXXX-XXXX"
              maxLength={9}
              autoComplete="off"
              invalid={Boolean(error) || undefined}
              aria-describedby="tf-backup-hint"
              value={backup}
              onChange={(event) => {
                setBackup(event.target.value);
              }}
            />
            <p className="hint" id="tf-backup-hint">
              {t("auth.twoFactor.backupHint")}
            </p>
          </div>
        )}
        <div className="a-row">
          <label className="a-chk">
            <input
              type="checkbox"
              className="checkbox"
              checked={trust}
              onChange={(event) => {
                setTrust(event.target.checked);
              }}
            />
            {t("auth.twoFactor.trust")}
          </label>
        </div>
        <Button type="submit" size="lg" className="w-full" loading={submitting} disabled={submitting || !ready}>
          {t("auth.twoFactor.submit")}
        </Button>
      </form>
      <p className="a-foot">
        <button type="button" className="a-link" onClick={toggleMode}>
          {mode === "app" ? t("auth.twoFactor.useBackup") : t("auth.twoFactor.useApp")}
        </button>
      </p>
      <RecoveryHelp challengeId={pending.challengeId} />
    </AuthLayout>
  );
}
